import type { Language } from "./translations";

export const LANGUAGE_COOKIE = "language";

const SUPPORTED: readonly Language[] = ["es", "en", "ca"];

export function parseLanguage(value: string | null | undefined): Language | null {
  if (value === "es" || value === "en" || value === "ca") return value;
  return null;
}

/** Normalize a BCP-47 tag to its primary language subtag (e.g. ca-ES, ca_ES → ca). */
export function normalizeLanguageTag(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  return trimmed.replace(/_/g, "-").split("-")[0]?.toLowerCase() ?? "";
}

/** Match a single tag against supported languages, or null. */
export function matchLanguageFromTag(raw: string): Language | null {
  const code = normalizeLanguageTag(raw);
  if ((SUPPORTED as readonly string[]).includes(code)) {
    return code as Language;
  }
  return null;
}

/** First supported language in tag order, or null. */
export function matchLanguageFromTags(tags: Iterable<string>): Language | null {
  for (const raw of tags) {
    const lang = matchLanguageFromTag(raw);
    if (lang) return lang;
  }
  return null;
}

type AcceptLanguageEntry = { tag: string; q: number };

function parseAcceptLanguageEntries(header: string): AcceptLanguageEntry[] {
  return header
    .split(",")
    .map((part) => {
      const segments = part.trim().split(";").map((s) => s.trim());
      const tag = segments[0] ?? "";
      let q = 1;
      for (let i = 1; i < segments.length; i++) {
        const match = segments[i].match(/^q=([\d.]+)$/i);
        if (match) {
          const parsed = parseFloat(match[1]);
          if (Number.isFinite(parsed)) q = parsed;
        }
      }
      return { tag, q };
    })
    .filter((entry) => entry.tag.length > 0);
}

/**
 * Parse an Accept-Language header (quality-sorted) and return the best supported language.
 */
export function parseAcceptLanguage(
  header: string | null | undefined
): Language | null {
  if (!header?.trim()) return null;

  const entries = parseAcceptLanguageEntries(header);
  entries.sort((a, b) => b.q - a.q);

  return matchLanguageFromTags(entries.map((e) => e.tag));
}

/**
 * Resolve initial UI language: cookie wins, then Accept-Language, then Spanish.
 */
export function resolveInitialLanguage(options: {
  cookie: string | null | undefined;
  acceptLanguage: string | null | undefined;
}): Language {
  const fromCookie = parseLanguage(options.cookie);
  if (fromCookie) return fromCookie;
  return parseAcceptLanguage(options.acceptLanguage) ?? "es";
}

/** Read the language cookie on the client (document.cookie). */
export function readLanguageCookie(): Language | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [name, ...valueParts] = part.trim().split("=");
    if (name !== LANGUAGE_COOKIE) continue;
    const value = valueParts.join("=");
    return parseLanguage(decodeURIComponent(value));
  }
  return null;
}

/** Client-safe cookie setter (matches server-readable `language` cookie). */
export function buildLanguageCookie(lang: Language): string {
  return `${LANGUAGE_COOKIE}=${lang};path=/;max-age=31536000;SameSite=Lax`;
}
